import * as React from 'react';
import { observer } from 'mobx-react';
import { Modal, ModalChildStyle } from '../modal';
import { classes, style } from 'typestyle';
import { colorBlack, colorBrand, colorCrimson, colorSlate, colorSubtle, colorWhite } from '../../theme/color';
import { SVGIcon } from '../svg-icon';
import { TextNoOrphan } from '../text-no-orphan';
import { em, percent, px } from 'csx';
import { Logo } from '../logo';
import { center, centerCenter, horizontal, horizontallySpaced, vertical, verticallySpaced, flex } from 'csstips';
import GoogleSignIn, { SignInType } from './google-sign-in';
import { error } from 'util';
import { FormInputCheckbox } from '../form-input-checkbox';
import { Collapsible } from '../collapsible/collapsible';
import { LabelledCheckbox } from '../form/labelled-checkbox';
import { CollabsibleSummaryLabelStyle } from '../collapsible/styles';
import { defaultLink } from '../../theme/mixin';

export interface AuthModalModel {
  show: boolean;
  youtubeLoginURL: string;
}

export interface AuthModalProps {
  model: AuthModalModel;

  onClickSignUp(event: React.MouseEvent<HTMLAnchorElement>): void;

  onRequestClose(): void;

  onClickTermsConditions(event: React.MouseEvent<any>): void;

  onClickPrivacyPolicy(event: React.MouseEvent<any>): void;

  i18n?: {
    title: string;
    jaackMaateTitle: string;
    vidsummitTitle: string;
    blogosphereTitle: string;
    vidconTitle: string;
    google: string;
    termsConditions: string;
    privacyPolicy: string;
    benefits: Array<{
      heading: string;
      content: string;
    }>;
  };
}

export interface AuthModalState {
  marketingCheckbox: boolean;
  termsCheckbox: boolean;
  hasClicked: boolean;
}

@observer
export class AuthModal extends React.Component<AuthModalProps, AuthModalState> {
  state = {
    marketingCheckbox: false,
    termsCheckbox: false,
    hasClicked: false,
  };

  setCheckbox(id: 1 | 2, value: boolean) {
    const newState: { marketingCheckbox?: boolean; termsCheckbox?: boolean } = {};
    switch (id) {
      case 1:
        newState.marketingCheckbox = value;
        break;
      case 2:
        newState.termsCheckbox = value;
    }
    this.setState(newState as AuthModalState);
  }

  render() {
    const { model, onRequestClose } = this.props;
    const { marketingCheckbox, termsCheckbox, hasClicked } = this.state;

    const isValid = !hasClicked || (hasClicked && termsCheckbox);

    return (
      <Modal show={model.show} onRequestClose={onRequestClose} className={AuthModal.styles.modal}>
        {model.show && (
          <div className={AuthModal.styles.container}>
            <h4>Join the music revolution!</h4>
            <LabelledCheckbox checked={marketingCheckbox} onChange={() => this.setCheckbox(1, !marketingCheckbox)}>
              I wish to receive updates about new content on Lickd
            </LabelledCheckbox>
            <LabelledCheckbox
              checked={termsCheckbox}
              onChange={() => this.setCheckbox(2, !termsCheckbox)}
              isValid={isValid}
            >
              I accept the <a href="/terms-and-conditions">Terms and Conditions</a> and{' '}
              <a href="/privacy-policy">Privacy Policy</a>
            </LabelledCheckbox>
            <p className={AuthModal.styles.termsError}>{isValid ? '\u00a0' : 'Please agree to the terms'}</p>
            <GoogleSignIn onClick={this.registerSignUpClick.bind(this)} />
            <Collapsible className={AuthModal.styles.collapsible}>
              <Collapsible.Summary indicator={true} className={AuthModal.styles.collapsibleSummary}>
                <SVGIcon.Warning color={colorBlack.toHexString()} />
                <p>Why register with Google?</p>
              </Collapsible.Summary>
              <div className={AuthModal.styles.collapsibleContent}>
                Lickd connects to your YouTube channel in order to clear claims quickly for you{' '}
                <a href="#">Learn More</a>
              </div>
            </Collapsible>
            <div className={AuthModal.styles.authority}>
              <SVGIcon.YouTubeCertified color={colorBlack.toHexString()} />
            </div>
          </div>
        )}
      </Modal>
    );
  }

  registerSignUpClick(evt: React.MouseEvent<HTMLAnchorElement>, type: SignInType) {
    if (type !== SignInType.REGISTER) return;
    this.setState({
      hasClicked: true,
    });
    const { termsCheckbox, marketingCheckbox } = this.state;
    window.sessionStorage.setItem('lickd_upfront_onboarding', JSON.stringify({ termsCheckbox, marketingCheckbox }));
    if (!termsCheckbox) {
      evt.preventDefault();
    }
  }

  static styles = {
    modal: style({ width: px(288), $nest: { '> div': { padding: px(16) } } }),
    container: style({
      ...ModalChildStyle,
      ...center,
      overflowY: 'hidden',
      maxWidth: 'calc(100vw - 4rem)',
      $nest: {
        '& a': defaultLink,
      },
    }),
    termsError: style({
      display: 'block',
      width: percent(100),
      fontSize: px(12),
      marginTop: px(2),
      color: colorCrimson.toHexString(),
    }),
    authority: style({
      ...centerCenter,
      ...horizontal,
      ...horizontallySpaced(30),
      margin: '16px 0',
      $nest: {
        '& path': {
          fill: colorSlate.toString(),
        },
        '& path:nth-child(4)': {
          fill: colorWhite.toString(),
        },
      },
    }),
    collapsible: style({
      width: percent(100),
    }),
    collapsibleSummary: style({
      ...CollabsibleSummaryLabelStyle,
      padding: px(8),
    }),
    collapsibleContent: style({
      fontSize: px(12),
      lineHeight: px(16),
      padding: '0 15px 17px',
      textAlign: 'center',
      $nest: {
        a: {
          display: 'block',
        },
      },
    }),
  };
}
